/**
 * api/deals.ts — the CRM pipeline calls (2026-07-21, clinic-service `/v1/deals`).
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: CRM pipeline (PR 3/5).
 *
 * All staff-authed (CLINICIAN + ADMIN — both roles work the pipeline; Create
 * New is CLINICIAN-only, server-enforced). Local `[CONTRACTS GAP]` type
 * mirrors live in api/types.ts. The server's deal-stages map is the control
 * (R41) — src/pipeline/stages.ts mirrors it purely for UI affordance.
 */
import { ApiError, get, post, request } from './client';
import type { DropOffReasonKey } from '../journey/dropOffReasons';
import type {
  AssessmentAnswers,
  DealActivityCreated,
  DealActivityCreateRequest,
  DealAttachmentContentType,
  DealCard,
  DealCounts,
  DealLaneMoneyResponse,
  DealCreateRequest,
  DealCreated,
  DealDetail,
  DealHistory,
  DealHistoryRevealRequest,
  DealHistoryRevealResult,
  DealList,
  DealMarkDuplicateResult,
  DealMarkTestResult,
  DealStage,
  LeadCallRequest,
  LeadCallResult,
  MarketingConsentWithdrawRequest,
  PhotoUploadGrant,
  StaffMarketingConsent,
} from './types';

/**
 * GET /v1/clinic/deals[?stage=][&includeTest=true] — the board's cards,
 * newest first. The DEFAULT list excludes test records (the server closes a
 * marked deal and hides it from the working board); `includeTest: true` is
 * the board's opt-in "Show test records" view — an explicit re-fetch, never
 * the default, so a demo walk-through can never sit on the working board
 * looking like a patient.
 */
/**
 * PER-LANE PAGE SIZE (2026-08-31; raised to 25 on 2026-09-04).
 *
 * The board renders eleven lanes. The server used to bound the take over the
 * WHOLE board, so a lane holding only older deals rendered empty while its
 * badge — counted over the whole tenant — correctly said how many it held. The
 * CTO saw "PLAN SENT — AWAITING PATIENT · 0 OF 10" with no filter applied: ten
 * patients waiting on a clinician and not one of them on screen.
 *
 * 25 rather than the server's 50 maximum (2026-09-04, CTO looking at the live
 * board): the first page should cover a normal working lane outright, and 20
 * was landing just under it. It is still half the cap, so the worst-case read
 * is 25 x 11 = 275 rows, and a lane deeper than one page is now REACHABLE
 * rather than merely admitted — see `offset` below.
 */
export const BOARD_PER_LANE = 25;

/**
 * THE SERVER'S CEILING ON `offset` (clinic-service `DEAL_LANE_OFFSET_MAX`).
 *
 * Past it the read is a 400, so the board stops OFFERING another page rather
 * than sending one that cannot succeed — 1000/25 = forty pages down a single
 * lane, which no working board reaches, but a control that 400s on press is
 * worse than a control that is not there.
 */
export const DEAL_LANE_OFFSET_MAX = 1000;

/**
 * PAGING ONE LANE (2026-09-04, clinic-service `feat/deal-lane-offset`).
 *
 * `offset` skips within ONE lane's page — it is NOT a whole-board cursor — so
 * the server REQUIRES `perLane` beside it (a page per stage) and refuses it
 * next to `limit`. Both routes take the same field under the same name, and
 * both keep the response shape: a flat `{items}` the board goes on grouping by
 * stage. There is no total and no `hasMore` flag; a lane has another page when
 * the page just returned for it was FULL. Order is `createdAt desc, id desc`
 * and stable across pages, so page N+1 never repeats page N.
 *
 * ⚠️ A ZERO OFFSET IS OMITTED FROM THE WIRE, AND THAT IS NOT TIDINESS. The GET
 * query schema is `.strict()`: an unknown param is a 400. The portal and
 * clinic-service deploy independently and the portal merges first, so there is
 * a real window where this build talks to a clinic-service that has never heard
 * of `offset`. Sending `offset=0` on every board load would 400 the LIST read
 * in that window — which sets `status: 'error'` and swaps the whole eleven-lane
 * working board for "we could not load the pipeline", for every clinician and
 * admin on the tenant. Omitting the zero keeps page one identical to the read
 * that shipped before this change, so the only thing that can fail in that
 * window is a View-more PRESS — which the board reports locally and leaves
 * retryable. `offset: 0` WITH `perLane` is perfectly valid server-side; the
 * caller may pass `page * BOARD_PER_LANE` unconditionally and this decides.
 */
/**
 * THE ACTIVITY ORDER AND ITS KEYSET (2026-09-26, returning patients S4.5 —
 * clinic S4.1 #224, plan Addendum 3).
 *
 * clinic-service can now order a lane by `lastActivityAt desc nulls last,
 * createdAt desc, id desc`, so a returning patient's new questionnaire, an order
 * event or a callback lifts the card to the top of its lane. That order MOVES
 * while someone pages it, so it is paged with a KEYSET, never `offset`: "View
 * more" sends the LAST card of the lane as this board saw it (`activityBefore` =
 * its `lastActivityAt`, or the literal `none` for a card the boot sweep has not
 * stamped yet; `idBefore` = its `dealId`). A card bumped between pages moves
 * AHEAD of the cursor, so it is never repeated on page two; it turns up at the
 * top of the next page-one read, and the board dedupes by id.
 *
 * ⚠️ THE ACTIVITY ORDER IS OPT-IN SERVER-SIDE (Addendum 3): only `order=activity`
 * or the keyset reads it. Every other read keeps `createdAt desc`, which is why
 * the offset lane below still exists — it is the OLD-SERVER lane (see
 * `PipelineTab`'s fallback). The server refuses the contradictions:
 * `order=activity` with `offset`/`before`, and the keyset with `offset`.
 */
export interface DealActivityCursor {
  /** The cursor card's `lastActivityAt`, or `none` when it had none. */
  activityBefore: string;
  /** The cursor card's `dealId`. */
  idBefore: string;
}

/** Page one in the activity order, or the page after a keyset cursor. */
export type DealListPaging = { order: 'activity' } | { cursor: DealActivityCursor };

/**
 * The keyset for "everything after this card". `undefined` and `null` are the
 * same absence here: a card with no `lastActivityAt` sat in the unstamped
 * segment at the foot of the lane, which the wire spells `none`.
 */
export function activityCursorFor(card: Pick<DealCard, 'dealId' | 'lastActivityAt'>): DealActivityCursor {
  return { activityBefore: card.lastActivityAt ?? 'none', idBefore: card.dealId };
}

export function getDeals(
  stages?: readonly DealStage[],
  includeTest?: boolean,
  offset = 0,
  includeDuplicates?: boolean,
  dropOffReasons?: readonly DropOffReasonKey[],
  paging?: DealListPaging,
): Promise<DealList> {
  const params = [
    // Repeated param, one per stage. The server coerces a single occurrence
    // back into an array, so narrowing a selection down to one still works.
    ...(stages ?? []).map((s) => `stages=${encodeURIComponent(s)}`),
    ...(includeTest ? ['includeTest=true'] : []),
    /**
     * ⚠️ A SEPARATE PARAM FROM `includeTest`, AND IT MUST STAY SEPARATE
     * (2026-09-06). The server refuses to serve merged duplicates without it,
     * which is why the `closed_duplicate` lane rendered EMPTY on every board
     * even when cards were sitting in it — the column existed, the rows
     * existed, and nothing ever asked for them.
     *
     * Not folded into "Show test records": a duplicate is a REAL patient who
     * submitted twice and may have been charged twice; a test record is a
     * rehearsal that never happened. Staff hunting a patient's missing second
     * submission and staff auditing QA dry-runs are two different jobs, and one
     * shared flag would make each wade through the other's rows.
     *
     * Omitted rather than sent as "false" for the deploy-window reason the
     * `offset` docblock sets out: the query schema is `.strict()`, so a
     * clinic-service that predates this param would 400 the whole board read.
     */
    ...(includeDuplicates ? ['includeDuplicates=true'] : []),
    /**
     * The drop-off reason filter (T-2, 2026-09-11) — repeated, one per reason,
     * exactly like `stages`. Omitted when nothing is ticked, for the deploy-window
     * reason above: the GET schema is `.strict()`, so a clinic-service that
     * predates the field would 400 the whole board read for a filter nobody had
     * applied.
     */
    ...(dropOffReasons ?? []).map((r) => `dropOffReasons=${encodeURIComponent(r)}`),
    // A page PER LANE, not one page for the board — see BOARD_PER_LANE.
    `perLane=${BOARD_PER_LANE}`,
    // Coerced from the string by the server's schema, so the query form is
    // enough. Omitted at zero — see the docblock.
    ...(offset > 0 ? [`offset=${offset}`] : []),
    // The activity order (S4.5). Page one asks for it by name; a "View more"
    // sends ONLY the keyset, which implies it. Never beside `offset` — the
    // server 400s that pair, and the caller never builds it.
    ...(paging && 'order' in paging ? ['order=activity'] : []),
    ...(paging && 'cursor' in paging
      ? [
          `activityBefore=${encodeURIComponent(paging.cursor.activityBefore)}`,
          `idBefore=${encodeURIComponent(paging.cursor.idBefore)}`,
        ]
      : []),
  ];
  const query = params.length > 0 ? `?${params.join('&')}` : '';
  return get(`/v1/clinic/deals${query}`, true);
}

/**
 * POST /v1/clinic/deals/search — the board's free-text contact search.
 *
 * ⚠️ A POST FOR A READ, AND THE TERM MUST NEVER GO IN A URL. It is a patient's
 * name or email. GCLB request logging runs at `sample_rate = 1.0` on every
 * backend, records the full request URL including the query string — verified
 * against live production logs — and a catch-all sink copies every entry into a
 * bucket with **400-day retention**. GCLB cannot redact a single query
 * parameter; logging is on or off per backend service. So `?q=Jane%20Smith`
 * would sit in an infrastructure log for thirteen months with no way to remove
 * it after the fact.
 *
 * Request bodies are not written to access logs. `POST /v1/identity/admin/users/lookup`
 * is the same pattern in this estate for the same reason.
 *
 * ⚠️ `getDeals` ABOVE NO LONGER TAKES A TERM AT ALL, and the server has dropped
 * `q` from its GET query schema to match. There is exactly one way to search,
 * and it keeps the name out of the URL by construction rather than by everyone
 * remembering to.
 *
 * The 2..120 bounds mirror the server contract: a single character is a 400
 * (so the first letter of a name would error before it searched), and staff
 * paste — signature blocks, spreadsheet rows — so an over-long term is sliced
 * to what the person meant rather than sent to be rejected.
 *
 * ⚠️ THIS ROUTE TAKES AN `offset` TOO, AND THE PORTAL DELIBERATELY NEVER SENDS
 * ONE. The body field is identical in name and meaning to the GET's — it pages
 * ONE lane, it requires `perLane`, it refuses `limit` — with one difference
 * worth writing down: the body schema does NOT coerce it. `"25"` there is a
 * 400, because in a JSON body a stringified integer is a caller bug rather
 * than the wire artefact a query string legitimately is.
 *
 * No parameter for it here, because there is nothing to call it: the board
 * withholds its per-lane "View more" while a search is running (see
 * `laneViewMore` in PipelineBoard — with a term on the wire `items` is the
 * post-filter list, so "rendered" stops meaning "loaded"), so a searched board
 * is never paged. An unused optional parameter would be a path no test could
 * reach. The day the board does page a searched lane, add it here, send a
 * NUMBER, and omit a zero for the same deploy-window reason `getDeals` does —
 * the deals stub already mirrors both refusals, so getting it wrong fails
 * locally.
 */
export function searchDeals(
  q: string,
  includeTest?: boolean,
  stages?: readonly DealStage[],
  includeDuplicates?: boolean,
  dropOffReasons?: readonly DropOffReasonKey[],
  /**
   * S4.5: a searched board is ordered by activity too, so a search result reads
   * in the same order as the board it replaced. Omitted when false (the
   * old-server fallback), because the body schema is `.strict()`.
   */
  activityOrder?: boolean,
): Promise<DealList> {
  return post(
    '/v1/clinic/deals/search',
    {
      q: q.trim().slice(0, 120),
      ...(includeTest ? { includeTest: true } : {}),
      // See `getDeals` for why this is a separate flag from `includeTest` and
      // why it is omitted rather than sent false. It matters MORE here than on
      // the board read: the canonical picker searches by name and email, and a
      // search that silently hid merged cards would let staff point a second
      // duplicate at a card that is already one — a refusal the server then
      // has to explain, for a row the portal could see and chose not to show.
      ...(includeDuplicates ? { includeDuplicates: true } : {}),
      // The panel's stage scope rides the same request, so "search WITHIN these
      // stages" is one read rather than a search followed by a client-side
      // narrowing that would re-introduce the page problem.
      ...(stages?.length ? { stages: [...stages] } : {}),
      // The reason filter rides the search too (2026-09-11). The body is
      // `.strict()` server-side, which is why clinic-service declares the field
      // on BOTH routes; omitted when empty, for `getDeals`' deploy-window reason.
      ...(dropOffReasons?.length ? { dropOffReasons: [...dropOffReasons] } : {}),
      // A searched board is still a board: page it per lane too, or a search
      // that matches many people in one stage starves every other lane.
      perLane: BOARD_PER_LANE,
      ...(activityOrder ? { order: 'activity' as const } : {}),
    },
    { auth: true },
  );
}

/** GET /v1/clinic/deals/counts — per-stage board badges. */
export function getDealCounts(): Promise<DealCounts> {
  return get('/v1/clinic/deals/counts', true);
}

/**
 * GET /v1/clinic/deals/lane-money — per-lane money behind the APPROVED cards.
 *
 * ⚠️ ADMIN ONLY, AND THE GUARD IS THE SERVER'S. clinic-service declares this
 * route `@Roles('ADMIN')` — a CLINICIAN gets a 403, not a stripped payload —
 * which is why the money is not a field on `getDealCounts` above: that read is
 * CLINICIAN+ADMIN, and anything money-shaped riding it would need redacting per
 * role on every future edit. Callers must still gate the CALL on the role
 * (see PipelineTab): firing it as a clinician earns a pointless 403 on every
 * board load. The gate here is belt; the server's guard is braces.
 */
export function getDealLaneMoney(): Promise<DealLaneMoneyResponse> {
  return get('/v1/clinic/deals/lane-money', true);
}

/** GET /v1/clinic/deals/:id — the drawer aggregate (deal + summaries + merged activity). */
export function getDealDetail(dealId: string): Promise<DealDetail> {
  return get(`/v1/clinic/deals/${encodeURIComponent(dealId)}`, true);
}

/**
 * PATCH /v1/clinic/deals/:id/stage — pure manual moves (closed_lost needs a
 * reason). `parseError` (the established 400/409/422 lane): the §9
 * three-attempt gate refuses an early closed_lost with a 409 whose `message`
 * is a PHI-free counts sentence ("N of 3 required call attempts…") the
 * pipeline surfaces inline.
 */
export function patchDealStage(
  dealId: string,
  stage: DealStage,
  reason?: string,
  reasonCode?: string,
): Promise<DealCard> {
  return request(`/v1/clinic/deals/${encodeURIComponent(dealId)}/stage`, {
    method: 'PATCH',
    // ⚠️ `reasonCode` IS OMITTED WHEN ABSENT, never sent empty. The server treats
    // an absent code as "this caller said nothing about why", which keeps the
    // OLD blanket three-attempt rule for it — deliberately, so a client that
    // predates the picker cannot close a deal at zero attempts. An empty string
    // would fail its enum instead, turning a safe fallback into a 400.
    body: { stage, ...(reason ? { reason } : {}), ...(reasonCode ? { reasonCode } : {}) },
    auth: true,
    parseError: true,
  });
}

/** POST /v1/clinic/deals/:id/handover — clinician↔admin with the required note. */
export function dealHandover(
  dealId: string,
  to: 'admin' | 'clinician',
  note: string,
  scheduledCallbackAt?: string,
): Promise<DealCard> {
  return post(
    `/v1/clinic/deals/${encodeURIComponent(dealId)}/handover`,
    { to, note, ...(scheduledCallbackAt ? { scheduledCallbackAt } : {}) },
    { auth: true },
  );
}

/** POST /v1/clinic/deals/:id/convert-to-callback — mint the ticket ("want our clinician to call you?"). */
export function convertDealToCallback(
  dealId: string,
  opts: { phone?: string; preferredTime?: string } = {},
): Promise<DealCard> {
  return post(`/v1/clinic/deals/${encodeURIComponent(dealId)}/convert-to-callback`, opts, { auth: true });
}

/** POST /v1/clinic/deals — clinician "Create New" (the SQL sales lane; contact + deal only). */
export function createDeal(body: DealCreateRequest): Promise<DealCreated> {
  return post('/v1/clinic/deals', body, { auth: true });
}

/**
 * POST /v1/clinic/deals/:id/recover — the hard-stop safety net undo
 * (2026-08-06): bring a Closed-Lost lead back into an active pipeline stage.
 *
 * WHICH STAGE IS THE SERVER'S CHOICE AND IT IS NO LONGER ONE ANSWER
 * (2026-09-05). An unpaid lead lands in `clinical_review` — the same "work it"
 * lane an `unreachable` revive targets, mid-pipeline rather than back at `mql`,
 * because a recovered lead has already been worked. A deal carrying an
 * `orderCaseRef` lands in `awaiting_admin_followup` instead: it can only have
 * reached `closed_lost` through a clinical REJECTION of a paid order, so there
 * is money in orders-service for an admin to sort out, and dropping it back
 * into the clinical lane would mint a card the paid-card rule then refuses to
 * move. The portal renders whatever stage comes back on the returned card.
 *
 * A REQUIRED `reason` is captured for the audit trail. `parseError` so a 409
 * (only a lost deal can be recovered) surfaces its PHI-free message inline.
 */
export function recoverDeal(dealId: string, reason: string): Promise<DealCard> {
  return post(`/v1/clinic/deals/${encodeURIComponent(dealId)}/recover`, { reason }, { auth: true, parseError: true });
}

/**
 * POST /v1/clinic/deals/:id/pending-admin-review — the clinician→admin handoff
 * (2026-08-07, CTO). A clinician must NEVER stop a patient's plan (that is a
 * money action, ADMIN-only, and it lives in orders-service), so when the
 * double-prescribing advisory shows a duplicate they cannot action, they leave
 * a REQUIRED `note` (3..2000 server-side) and hand the deal to an admin: it
 * moves into the `pending_admin_review` stage and the note lands on the deal
 * timeline for both roles to read.
 *
 * A workflow move plus a note and NOTHING else — no order, subscription or plan
 * is touched, and the patient lane is never blocked or told.
 *
 * `parseError` so the server's PHI-free 409 (a terminal deal cannot be handed
 * over) surfaces inline, exactly as `recoverDeal` above does.
 */
export function dealPendingAdminReview(dealId: string, note: string): Promise<DealCard> {
  return post(
    `/v1/clinic/deals/${encodeURIComponent(dealId)}/pending-admin-review`,
    { note },
    { auth: true, parseError: true },
  );
}

/**
 * POST /v1/clinic/deals/:id/mark-test — mark the deal as a TEST record
 * (2026-08-26, reporting hygiene): staff walk-throughs and demo runs land on
 * the live board looking exactly like patients, so ADMIN/CLINICIAN can mark
 * one as test data. The server sets `isTest`, CLOSES an open deal
 * (closed_lost, reason "test record") and excludes it from every
 * analytics/reporting read AND the default deals list — the card disappears
 * from the working board (visible again only through the board's "Show test
 * records" view, `includeTest=true` on getDeals). Nothing is deleted and no
 * email is sent.
 * `reason` is OPTIONAL and lands on the server-side audit row only (R19).
 * `parseError` so a PHI-free refusal (e.g. already marked) surfaces inline,
 * the same lane recoverDeal/dealPendingAdminReview use.
 *
 * `revert: true` (2026-08-26, clinic#137) UNMARKS a flagged record — a
 * FLAG-ONLY write: the server clears `isTest` so the deal counts as a real
 * record again, and the stage NEVER changes (a mark-closed deal stays
 * closed_lost; reopening it is recoverDeal's job — which also clears the
 * flag — so the two undo lanes never overlap). The flag rides the SAME
 * endpoint (the server owns both directions, R41) and is omitted from the
 * body entirely on a plain mark, so an older clinic-service that predates it
 * never sees an unknown key.
 *
 * ⚠️ THE RESPONSE IS `{deal, cascade}`, NOT A CARD (2026-09-06). It was typed
 * `Promise<DealCard>` here for two releases while clinic-service was already
 * answering with the cascade — so the six per-leg outcomes its controller
 * docblock calls "WHAT THE PORTAL MUST RENDER" were parsed off the wire and
 * dropped on the floor, and a WITHHELD patient leg or a FAILED plan leg
 * rendered as an unqualified success on a half-marked record. `cascade` is
 * OPTIONAL on the type on purpose: an older server sends a bare card, and the
 * caller must treat its absence as "we cannot tell", never as "all clean".
 * See DealMarkTestResult, and pipeline/markTestCascade for the rendering.
 */
export function markDealTest(
  dealId: string,
  reason?: string,
  opts: { revert?: boolean } = {},
): Promise<DealMarkTestResult> {
  return post<DealMarkTestResult>(
    `/v1/clinic/deals/${encodeURIComponent(dealId)}/mark-test`,
    { ...(reason ? { reason } : {}), ...(opts.revert ? { revert: true } : {}) },
    { auth: true, parseError: true },
  );
}

/**
 * POST /v1/clinic/deals/:id/mark-duplicate — **the same real patient submitted
 * twice**, so point the second card at the first (2026-09-06, CTO — the Asana
 * "merge duplicate tasks" model).
 *
 * ⚠️ THIS IS NOT `markDealTest`, AND THE TWO MUST NEVER BE MERGED. That action
 * means "this never happened" and marks three records — the deal, the ORDER and
 * the patient ACCOUNT. This one means "this happened TWICE, to a real person",
 * so it marks exactly one: the deal. Marking a double-submitter as a test
 * patient would delete a genuine human from the directory and take a REAL
 * CHARGE off the payments board, concealing a double-charge that probably needs
 * refunding. That is why the response carries `payment` and why the confirm
 * dialog says the money stays visible.
 *
 * The mark also CLOSES the card out of the pipeline onto `closed_duplicate`,
 * banking the lane it came from so the un-mark can put it back where it was.
 *
 * `revert: true` is the undo, and unlike the test mark's it is a genuine
 * restore — the card returns to its banked lane. Staff WILL mis-pick a
 * canonical out of a list of similar names, so the undo is not optional
 * furniture; it is half the feature.
 *
 * EXACTLY ONE of `canonicalDealId` / `revert` — the server's schema refines it,
 * so a body carrying both or neither is a 400. This function's two-shape
 * signature makes that unrepresentable from the portal.
 *
 * `parseError` because EVERY refusal here is a sentence staff have to act on,
 * not a failure to retry: self-reference, a loop, an unknown canonical, "this
 * deal is the canonical for N other duplicates — un-mark those first", and "the
 * canonical is a test record". See `duplicateRefusal` in duplicateSurface.tsx
 * for how they are rendered.
 */
export function markDealDuplicate(
  dealId: string,
  input: { canonicalDealId: string; reason?: string } | { revert: true; reason?: string },
): Promise<DealMarkDuplicateResult> {
  return post(
    `/v1/clinic/deals/${encodeURIComponent(dealId)}/mark-duplicate`,
    {
      ...('canonicalDealId' in input ? { canonicalDealId: input.canonicalDealId } : { revert: true as const }),
      ...(input.reason ? { reason: input.reason } : {}),
    },
    { auth: true, parseError: true },
  );
}

/**
 * POST /v1/clinic/deals/:id/activities — HubSpot-grade logging on ANY deal
 * (2026-07-21 UX pass; contract PINNED): note / call attempt / email. `body`
 * is REQUIRED (1..2000) for every kind; `outcome` REQUIRED iff call_attempt.
 * Wave 4 [CONTRACTS GAP]: optional `attachmentRefs` (max 5) — object refs
 * from COMPLETED attachment-upload grants ride the note; feed entries echo
 * them as `attachments: [{ref, url|null}]` (url = a signed read link).
 * Entries surface through the SAME merged feed the drawer aggregate returns.
 */
export function postDealActivity(dealId: string, body: DealActivityCreateRequest): Promise<DealActivityCreated> {
  return post(`/v1/clinic/deals/${encodeURIComponent(dealId)}/activities`, body, { auth: true });
}

/**
 * POST /v1/clinic/deals/:id/activities/attachment-upload — a signed PUT grant
 * for ONE note attachment (Wave 4 [CONTRACTS GAP]). `contentType` is limited
 * to the four allowlisted types; the response reuses the photo-grant DTO:
 * `{stub: true}` when object storage isn't configured (attach nothing, show
 * the stub notice), else `{stub: false, uploadUrl, objectRef, expiresAt}`.
 */
export function grantDealAttachmentUpload(
  dealId: string,
  contentType: DealAttachmentContentType,
): Promise<PhotoUploadGrant> {
  return post(`/v1/clinic/deals/${encodeURIComponent(dealId)}/activities/attachment-upload`, { contentType }, { auth: true });
}

/** GET /v1/clinic/assessments/:id/answers — latest raw values for the single-page form prefill. */
export function getAssessmentAnswers(assessmentId: string): Promise<AssessmentAnswers> {
  return get(`/v1/clinic/assessments/${encodeURIComponent(assessmentId)}/answers`, true);
}

/**
 * POST /v1/clinic/deals/:id/lead-call — the SECONDARY warm-lead calling lane
 * (2026-09-08, CTO) [CONTRACTS GAP].
 *
 * ⚠️ NOT `convertDealToCallback` BELOW/ABOVE — read this before consolidating
 * the two. That one mints a REAL `CallbackRequest` with a 3-then-3 escalation
 * machine behind it, for a patient who ASKED us to ring them, and its ticket
 * lands on the ops callbacks queue. This one records a conversion call on a
 * lead who did not ask, mints nothing, and leaves that queue untouched. Same
 * two roles, different jobs.
 *
 * The response's `nextStep` is an instruction to the caller: `'send_plan_link'`
 * means the deal was deliberately NOT moved and the operator should now send the
 * patient their plan link through the control that already owns sending it.
 *
 * `parseError` because EVERY refusal this route makes is a sentence the desk has
 * to act on rather than a generic failure: the 409 on an active call round names
 * the surface to use instead ("log the call on the round"), the 409 on a
 * terminal deal names the recover path, and the 400s name which of the outcome /
 * disposition / reason rules the body broke. Without it the dialog could only
 * say "something went wrong" while the patient is still on the phone.
 */
export function logLeadCall(dealId: string, body: LeadCallRequest): Promise<LeadCallResult> {
  return post(`/v1/clinic/deals/${encodeURIComponent(dealId)}/lead-call`, body, {
    auth: true,
    parseError: true,
  });
}

/**
 * GET /v1/clinic/deals/:id/marketing-consent — the patient's marketing consent
 * per channel plus its history (ADMIN, CLINICIAN; 2026-09-11). Read-only facts
 * about what the PATIENT chose; the drawer never infers a "no" from a failure.
 */
export function getDealMarketingConsent(dealId: string): Promise<StaffMarketingConsent> {
  return get(`/v1/clinic/deals/${encodeURIComponent(dealId)}/marketing-consent`, true);
}

/**
 * POST /v1/clinic/deals/:id/marketing-consent/withdraw — record that the patient
 * told staff they no longer want messages on one channel. There is NO grant twin
 * and there must never be one: consent has to come from the person. The server
 * writes the ledger row (source `staff`) and a `marketing_consent_withdrawn`
 * AuditEntry in one transaction, and answers the same shape as the GET.
 *
 * `parseError` so a PHI-free 400/404 sentence can reach the dialog.
 */
export function withdrawDealMarketingConsent(
  dealId: string,
  body: MarketingConsentWithdrawRequest,
): Promise<StaffMarketingConsent> {
  return post(`/v1/clinic/deals/${encodeURIComponent(dealId)}/marketing-consent/withdraw`, body, {
    auth: true,
    parseError: true,
  });
}

/**
 * GET /v1/clinic/deals/:id/history — the History tab's route (clinic S4.2, #225;
 * CLINICIAN·ADMIN, role-shaped server-side: only an ADMIN's answer carries
 * `touches` and `unlockLinksIssued`).
 *
 * ⚠️ 404 `{code:'disabled'}` IS AN ANSWER, NOT A FAULT. clinic's
 * CONTACT_HISTORY_ENABLED is OFF by default and stays off until the DPIA is
 * signed off and identity's dispute producer is live (plan Addendum 2/4). The
 * drawer HIDES the route-backed view on it rather than showing an error — see
 * `historyUnavailable`.
 */
export function getDealHistory(dealId: string): Promise<DealHistory> {
  return get(`/v1/clinic/deals/${encodeURIComponent(dealId)}/history`, true);
}

/**
 * Is this failure the History route saying "not here"? Two 404s mean that and
 * are deliberately treated alike: `{code:'disabled'}` (the switch is off — every
 * environment today) and a bare 404 (a clinic-service older than S4.2, which has
 * no such route). Neither is an outage, and neither may render as one: the
 * drawer keeps today's History lists and shows nothing of the new view. Keyed
 * on the STATUS alone because the body is not needed to decide and the client
 * keeps no body (R19). Any other failure IS an outage and says so.
 */
export function historyUnavailable(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404;
}

/**
 * POST /v1/clinic/deals/:id/history/entries/:entryId/reveal — THE LOGGED CLICK.
 *
 * clinic-service writes `assessment_history_answers_viewed` {viaDealId,
 * matchBasis, reasonCode} in the SAME transaction and BEFORE reading a single
 * answer, then returns the answers projection (no date of birth, no photos, no
 * identity). The body is REQUIRED: a closed `reason` and an optional `note`
 * (≤200, REQUIRED for `other`) — staff free text that lands on the audit row's
 * `reason` column only. Called ONLY from the dialog's confirm, never on render.
 * `parseError` so the opaque 404 and a 400 reach the dialog as sentences.
 */
export function revealDealHistoryEntry(
  dealId: string,
  entryId: string,
  body: DealHistoryRevealRequest,
): Promise<DealHistoryRevealResult> {
  return post(
    `/v1/clinic/deals/${encodeURIComponent(dealId)}/history/entries/${encodeURIComponent(entryId)}/reveal`,
    { reason: body.reason, ...(body.note ? { note: body.note } : {}) },
    { auth: true, parseError: true },
  );
}

/**
 * POST /v1/clinic/deals/:id/reopen-superseded — ADMIN ONLY (clinic S4.1 #224).
 * Put a card the SYSTEM closed because a newer questionnaire arrived back in
 * the lane it was closed from. A REQUIRED reason (3..2000), audited as
 * `deal_supersede_released {trigger: staff_reopen}`. 409 when the card was not
 * superseded — a STAFF duplicate is undone through mark-duplicate's release.
 * `parseError` so the server's sentence reaches the dialog verbatim.
 */
export function reopenSupersededDeal(dealId: string, reason: string): Promise<DealCard> {
  return post(`/v1/clinic/deals/${encodeURIComponent(dealId)}/reopen-superseded`, { reason }, {
    auth: true,
    parseError: true,
  });
}
